import React, { useEffect, useMemo, useState } from "react";
import { Integration } from "../../../../../../../redux/slices/projects/Types";
import StatusTag, { IntegrationStatusLabel } from "../shared/StatusTag";
import "./DevelopmentStep.scss";
import {
  Button,
  DatePicker,
  Divider,
  Dropdown,
  dropDownThemes,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Modal,
  TextArea,
} from "@hid-galaxy-ui/galaxy-react";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import {
  getTimelineEntries,
  mapTimelineEntryToStepEntry,
  getParentCategoryId,
  getCategoryId,
  formatDateForDB,
  formatDateForDisplay,
} from "../shared/timeline-helpers";
import { createTimelineEntrySagaAction, updateTimelineEntrySagaAction } from "../../../../../../../redux/saga/projects/saga-actions";
import { CreateTimelineEntryRequest } from "../../../../../../../redux/slices/projects/Types";
import { useTimelineStepPermissions } from "../../../../../../../hooks/useTimelineStepPermissions";
import { useResolveSubmittedBy } from "../shared/useResolveSubmittedBy";

export type DevelopmentEntry = {
  id?: number;
  category?: string;
  start_date: string | null;
  completion_date: string | null;
  status: IntegrationStatusLabel;
  notes?: string | null;
  submitted_by?: number | null;
  submission_date?: string | null;
  created_at?: string;
  process_name?: "Architecture review" | "Mid term assessment";
};

// Helper functions
export function getArchitectureReviewEntries(
  integration?: Integration
): DevelopmentEntry[] {
  const timelineEntries = getTimelineEntries(
    integration?.timeline,
    "Development",
    "Architecture review"
  );
  if (timelineEntries.length > 0) {
    return timelineEntries.map((entry) => ({
      ...mapTimelineEntryToStepEntry<DevelopmentEntry>(entry),
      process_name: "Architecture review" as const,
    }));
  }
  return [];
}

export function getMidTermAssessmentEntries(
  integration?: Integration
): DevelopmentEntry[] {
  const timelineEntries = getTimelineEntries(
    integration?.timeline,
    "Development",
    "Mid term assessment"
  );
  if (timelineEntries.length > 0) {
    return timelineEntries.map((entry) => ({
      ...mapTimelineEntryToStepEntry<DevelopmentEntry>(entry),
      process_name: "Mid term assessment" as const,
    }));
  }
  return [];
}

export function getDevelopmentEntries(integration?: Integration): {
  architectureReview: DevelopmentEntry[];
  midTermAssessment: DevelopmentEntry[];
} {
  return {
    architectureReview: getArchitectureReviewEntries(integration),
    midTermAssessment: getMidTermAssessmentEntries(integration),
  };
}

export function combineAndSortHistoryEntries(
  architectureReviewEntries: DevelopmentEntry[],
  midTermAssessmentEntries: DevelopmentEntry[]
): DevelopmentEntry[] {
  const combined = [...architectureReviewEntries, ...midTermAssessmentEntries];

  return combined.sort((a, b) => {
    const dateA = a.created_at || a.submission_date || "";
    const dateB = b.created_at || b.submission_date || "";

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    // Parse dates - handle multiple formats
    const momentA = moment(
      dateA,
      ["DD/MM/YYYY", "D/M/YYYY", "DD MMM, YYYY", moment.ISO_8601],
      true
    );
    const momentB = moment(
      dateB,
      ["DD/MM/YYYY", "D/M/YYYY", "DD MMM, YYYY", moment.ISO_8601],
      true
    );

    if (!momentA.isValid() && !momentB.isValid()) return 0;
    if (!momentA.isValid()) return 1;
    if (!momentB.isValid()) return -1;

    // Sort DESC (newest first)
    return momentB.valueOf() - momentA.valueOf();
  });
}

export function getDevelopmentCurrentStatus(
  integration?: Integration
): IntegrationStatusLabel | null {
  // Return null if timeline is empty or doesn't exist
  if (
    !integration?.timeline ||
    Object.keys(integration.timeline).length === 0
  ) {
    return null;
  }

  const architectureReviewEntries = getTimelineEntries(
    integration.timeline,
    "Development",
    "Architecture review"
  );
  const midTermAssessmentEntries = getTimelineEntries(
    integration.timeline,
    "Development",
    "Mid term assessment"
  );

  // Return null if both are empty
  if (
    architectureReviewEntries.length === 0 &&
    midTermAssessmentEntries.length === 0
  ) {
    return null;
  }

  const architectureReview =
    architectureReviewEntries.length > 0
      ? architectureReviewEntries.map((entry) =>
        mapTimelineEntryToStepEntry<DevelopmentEntry>(entry)
      )
      : [];
  const midTermAssessment =
    midTermAssessmentEntries.length > 0
      ? midTermAssessmentEntries.map((entry) =>
        mapTimelineEntryToStepEntry<DevelopmentEntry>(entry)
      )
      : [];

  const architectureReviewStatus = architectureReview[0]?.status || null;
  const midTermAssessmentStatus = midTermAssessment[0]?.status || null;

  // If both are null, return null
  if (!architectureReviewStatus && !midTermAssessmentStatus) {
    return null;
  }

  // One sub-category filled, the other empty — phase is still in progress
  if (
    (architectureReviewStatus && !midTermAssessmentStatus) ||
    (!architectureReviewStatus && midTermAssessmentStatus)
  ) {
    return "In progress";
  }

  // Both completed
  if (
    architectureReviewStatus === "Completed" &&
    midTermAssessmentStatus === "Completed"
  ) {
    return "Completed";
  }

  // One completed, one not applicable -> Completed
  if (
    (architectureReviewStatus === "Completed" &&
      midTermAssessmentStatus === "Not applicable") ||
    (architectureReviewStatus === "Not applicable" &&
      midTermAssessmentStatus === "Completed")
  ) {
    return "Completed";
  }

  // One completed, one On hold -> On hold
  if (
    (architectureReviewStatus === "Completed" &&
      midTermAssessmentStatus === "On hold") ||
    (architectureReviewStatus === "On hold" &&
      midTermAssessmentStatus === "Completed")
  ) {
    return "On hold";
  }

  // One completed, one In progress -> In progress
  if (
    (architectureReviewStatus === "Completed" &&
      midTermAssessmentStatus === "In progress") ||
    (architectureReviewStatus === "In progress" &&
      midTermAssessmentStatus === "Completed")
  ) {
    return "In progress";
  }

  // Return first non-completed status (priority: "In progress" > "On hold" > "Not applicable")
  const statuses = [architectureReviewStatus, midTermAssessmentStatus].filter(
    Boolean
  ) as IntegrationStatusLabel[];
  const inProgress = statuses.find((s) => s === "In progress");
  if (inProgress) return inProgress;

  const onHold = statuses.find((s) => s === "On hold");
  if (onHold) return onHold;

  // If both are "Not applicable", return "Not applicable"
  return statuses[0] || "Not applicable";
}

interface DevelopmentStepProps {
  integration?: Integration;
  isFormDisabled?: boolean;
}

const DevelopmentStep: React.FC<DevelopmentStepProps> = ({ integration, isFormDisabled = false }) => {
  const dispatch = useAppDispatch();
  const { projectDetails, status } = useAppSelector((state) => state.projects);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [architectureReviewOpen, setArchitectureReviewOpen] =
    useState<boolean>(false);
  const [midTermAssessmentOpen, setMidTermAssessmentOpen] =
    useState<boolean>(false);
  const [editingSection, setEditingSection] = useState<
    "architectureReview" | "midTermAssessment" | null
  >(null);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [commentsModalMode, setCommentsModalMode] = useState<
    "view" | "add" | "edit"
  >("view");
  const [commentsModalNote, setCommentsModalNote] = useState<string>("");
  const [commentsModalAddSection, setCommentsModalAddSection] = useState<
    "architectureReview" | "midTermAssessment" | null
  >(null);

  // Architecture review state
  const [architectureReviewNotesDraft, setArchitectureReviewNotesDraft] =
    useState<string>("");
  const [architectureReviewStartDate, setArchitectureReviewStartDate] =
    useState<Date | null>(null);
  const [
    architectureReviewCompletionDate,
    setArchitectureReviewCompletionDate,
  ] = useState<Date | null>(null);
  const [architectureReviewStatus, setArchitectureReviewStatus] =
    useState<IntegrationStatusLabel | null>(null);

  // Mid term assessment state
  const [midTermAssessmentNotesDraft, setMidTermAssessmentNotesDraft] =
    useState<string>("");
  const [midTermAssessmentStartDate, setMidTermAssessmentStartDate] =
    useState<Date | null>(null);
  const [midTermAssessmentCompletionDate, setMidTermAssessmentCompletionDate] =
    useState<Date | null>(null);
  const [midTermAssessmentStatus, setMidTermAssessmentStatus] =
    useState<IntegrationStatusLabel | null>(null);

  const { architectureReview, midTermAssessment } = useMemo(
    () => getDevelopmentEntries(integration),
    [integration]
  );

  const {
    canAccessTimeline,
    canAccessNotesHistory,
    timelineLocked,
    resolveCommentsModalMode,
  } = useTimelineStepPermissions(isFormDisabled);

  const [editingCommentIndex, setEditingCommentIndex] = useState<number | null>(
    null
  );
  const [commentDraft, setCommentDraft] = useState<string>("");

  const statusOptions = useMemo(
    () => [
      { label: "Completed", value: "Completed" as IntegrationStatusLabel },
      { label: "In progress", value: "In progress" as IntegrationStatusLabel },
      { label: "On hold", value: "On hold" as IntegrationStatusLabel },
      {
        label: "Not applicable",
        value: "Not applicable" as IntegrationStatusLabel,
      },
    ],
    []
  );

  const parseDateOrNull = (dateStr: string | null): Date | null => {
    if (!dateStr) return null;
    const m = moment(dateStr, ["DD/MM/YYYY", "D/M/YYYY", "DD MMM, YYYY"], true);
    if (m.isValid()) return m.toDate();
    const fallback = new Date(dateStr);
    return isNaN(fallback.getTime()) ? null : fallback;
  };

  useEffect(() => {
    const latestArchitectureReview = architectureReview?.[0] || null;
    const latestMidTermAssessment = midTermAssessment?.[0] || null;

    setArchitectureReviewStartDate(
      parseDateOrNull(latestArchitectureReview?.start_date || null)
    );
    setArchitectureReviewCompletionDate(
      parseDateOrNull(latestArchitectureReview?.completion_date || null)
    );
    setArchitectureReviewStatus(latestArchitectureReview?.status || null);

    setMidTermAssessmentStartDate(
      parseDateOrNull(latestMidTermAssessment?.start_date || null)
    );
    setMidTermAssessmentCompletionDate(
      parseDateOrNull(latestMidTermAssessment?.completion_date || null)
    );
    setMidTermAssessmentStatus(latestMidTermAssessment?.status || null);

    setEditingCommentIndex(null);
    setCommentDraft("");
  }, [architectureReview, midTermAssessment]);

  const resolveSubmittedByName = useResolveSubmittedBy(
    [...architectureReview, ...midTermAssessment].map((e) => e.submitted_by)
  );

  const combinedHistoryEntries = useMemo(() => {
    return combineAndSortHistoryEntries(architectureReview, midTermAssessment);
  }, [architectureReview, midTermAssessment]);

  const openCommentsModal = (
    mode: "view" | "add" | "edit",
    note?: string,
    editEntryIndex?: number,
    addSection?: "architectureReview" | "midTermAssessment"
  ) => {
    const effectiveMode = resolveCommentsModalMode(mode);
    const noteText =
      note ??
      (editEntryIndex !== undefined
        ? ((combinedHistoryEntries[editEntryIndex]?.notes || "") as string)
        : "");
    setCommentsModalMode(effectiveMode);
    setCommentsModalNote(noteText);
    if (effectiveMode === "edit" && editEntryIndex !== undefined) {
      setEditingCommentIndex(editEntryIndex);
      setCommentDraft(noteText.slice(0, 500));
    }
    if (effectiveMode === "add" && addSection) setCommentsModalAddSection(addSection);
    else setCommentsModalAddSection(null);
    setIsCommentsModalOpen(true);
  };

  const closeCommentsModal = () => {
    setIsCommentsModalOpen(false);
    setCommentsModalNote("");
    setCommentsModalAddSection(null);
    if (commentsModalMode === "edit") {
      setEditingCommentIndex(null);
      setCommentDraft("");
    }
  };

  const onRowEditStart = (section: "architectureReview" | "midTermAssessment") => {
    if (section === "architectureReview") {
      const latest = architectureReview?.[0] ?? null;
      setArchitectureReviewStartDate(parseDateOrNull(latest?.start_date ?? null));
      setArchitectureReviewCompletionDate(parseDateOrNull(latest?.completion_date ?? null));
      setArchitectureReviewStatus(latest?.status ?? null);
      setArchitectureReviewNotesDraft((latest?.notes as string) ?? "");
    } else {
      const latest = midTermAssessment?.[0] ?? null;
      setMidTermAssessmentStartDate(parseDateOrNull(latest?.start_date ?? null));
      setMidTermAssessmentCompletionDate(parseDateOrNull(latest?.completion_date ?? null));
      setMidTermAssessmentStatus(latest?.status ?? null);
      setMidTermAssessmentNotesDraft((latest?.notes as string) ?? "");
    }
    setEditingSection(section);
  };

  const onRowEditCancel = () => {
    const latestArch = architectureReview?.[0] ?? null;
    const latestMid = midTermAssessment?.[0] ?? null;
    setArchitectureReviewStartDate(parseDateOrNull(latestArch?.start_date ?? null));
    setArchitectureReviewCompletionDate(parseDateOrNull(latestArch?.completion_date ?? null));
    setArchitectureReviewStatus(latestArch?.status ?? null);
    setArchitectureReviewNotesDraft((latestArch?.notes as string) ?? "");
    setMidTermAssessmentStartDate(parseDateOrNull(latestMid?.start_date ?? null));
    setMidTermAssessmentCompletionDate(parseDateOrNull(latestMid?.completion_date ?? null));
    setMidTermAssessmentStatus(latestMid?.status ?? null);
    setMidTermAssessmentNotesDraft((latestMid?.notes as string) ?? "");
    setEditingSection(null);
  };

  const onRowEditConfirm = (section: "architectureReview" | "midTermAssessment") => {
    onUpdateClick(section);
    setEditingSection(null);
  };

  const onUpdateClick = (step: "architectureReview" | "midTermAssessment") => {
    if (!integration?.integration_id) {
      return;
    }

    const submittedBy = loggedUserDetail?.ID || 0;
    if (!submittedBy) {
      return;
    }

    const parentCategoryId = getParentCategoryId(
      integration?.timeline,
      "Development"
    );

    // Create entry for Architecture review if it has data
    // For "Not applicable", dates are optional.
    if (
      step === "architectureReview" &&
      architectureReviewStatus &&
      (architectureReviewStartDate || architectureReviewStatus === "Not applicable")
    ) {
      const isNotApplicable = architectureReviewStatus === "Not applicable";
      const startDateForPayload = architectureReviewStartDate
        ? formatDateForDB(architectureReviewStartDate)
        : "0000-00-00";
      const architectureReviewCategoryId = getCategoryId(
        integration?.timeline,
        "Development",
        "Architecture review"
      );
      if (!architectureReviewCategoryId) {
        return;
      }

      const architectureReviewPayload: CreateTimelineEntryRequest = {
        category: architectureReviewCategoryId,
        parent_category_id: parentCategoryId,
        submitted_by: submittedBy,
        start_date: startDateForPayload,
        completion_date: architectureReviewCompletionDate
          ? formatDateForDB(architectureReviewCompletionDate)
          : "",
        status: architectureReviewStatus,
        note: (architectureReviewNotesDraft || "").slice(0, 500),
      };
      dispatch(
        createTimelineEntrySagaAction(
          integration.integration_id,
          architectureReviewPayload
        )
      );
    }

    // Create entry for Mid term assessment if it has data
    // For "Not applicable", dates are optional.
    if (
      step === "midTermAssessment" &&
      midTermAssessmentStatus &&
      (midTermAssessmentStartDate || midTermAssessmentStatus === "Not applicable")
    ) {
      const isNotApplicable = midTermAssessmentStatus === "Not applicable";
      const startDateForPayload = midTermAssessmentStartDate
        ? formatDateForDB(midTermAssessmentStartDate)
        : "0000-00-00";
      const midTermAssessmentCategoryId = getCategoryId(
        integration?.timeline,
        "Development",
        "Mid term assessment"
      );
      if (!midTermAssessmentCategoryId) {
        return;
      }

      const midTermAssessmentPayload: CreateTimelineEntryRequest = {
        category: midTermAssessmentCategoryId,
        parent_category_id: parentCategoryId,
        submitted_by: submittedBy,
        start_date: startDateForPayload,
        completion_date: midTermAssessmentCompletionDate
          ? formatDateForDB(midTermAssessmentCompletionDate)
          : "",
        status: midTermAssessmentStatus,
        note: (midTermAssessmentNotesDraft || "").slice(0, 500),
      };
      dispatch(
        createTimelineEntrySagaAction(
          integration.integration_id,
          midTermAssessmentPayload
        )
      );
    }

  };

  const onCancelCommentEdit = () => {
    setEditingCommentIndex(null);
    setCommentDraft("");
  };

  const onSaveCommentEdit = () => {
    if (timelineLocked || editingCommentIndex === null) return;

    const entry = combinedHistoryEntries[editingCommentIndex];
    if (!entry?.id) {
      onCancelCommentEdit();
      return;
    }

    const payload = {
      note: (commentDraft || "").slice(0, 500),
    };

    dispatch(updateTimelineEntrySagaAction(entry.id, payload));
    setEditingCommentIndex(null);
    setCommentDraft("");
  };

  const architectureReviewLatest = architectureReview?.[0] ?? null;
  const midTermAssessmentLatest = midTermAssessment?.[0] ?? null;
  const canSaveArchitectureReview =
    editingSection === "architectureReview" &&
    !!architectureReviewStatus &&
    (architectureReviewStatus === "Not applicable" || !!architectureReviewStartDate) &&
    !timelineLocked &&
    !status.timelineUpdateLoading;
  const canSaveMidTermAssessment =
    editingSection === "midTermAssessment" &&
    !!midTermAssessmentStatus &&
    (midTermAssessmentStatus === "Not applicable" || !!midTermAssessmentStartDate) &&
    !timelineLocked &&
    !status.timelineUpdateLoading;

  return (
    <div className="development-step">

      {canAccessTimeline ? (
        <div className="development-edit">
          {/* Architecture review section */}
          <div className="development-subcategory">
            <div className="subcategory-header">
              <h4 className="subcategory-title">Architecture review</h4>
              <div className="subcategory-actions">
                {architectureReviewOpen ? (
                  <button
                    type="button"
                    className="close-link"
                    onClick={() => setArchitectureReviewOpen(false)}
                  >
                    
                    <Iconography icon="xmark" size={IconographySizes.Small} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="status-notes-link"
                    onClick={() => setArchitectureReviewOpen(true)}
                  >
                    Notes
                  </button>
                )}
              </div>
            </div>
            {architectureReviewOpen && (
              <div className="development-table-card onboarding-table-card">
                <div className="onboarding-table">
                  <div className="onboarding-table__row onboarding-table__header">
                    <div className="onboarding-table__cell submitted-by">Submitted by</div>
                    <div className="onboarding-table__cell status">Status</div>
                    <div className="onboarding-table__cell start">Start</div>
                    <div className="onboarding-table__cell completion">Completion</div>
                    <div className="onboarding-table__cell comments">Comments</div>
                    <div className="onboarding-table__cell action">Action</div>
                  </div>
                  <div className="onboarding-table__row onboarding-table__body-row">
                    <div className="onboarding-table__cell submitted-by">
                      {editingSection === "architectureReview"
                        ? resolveSubmittedByName(architectureReviewLatest?.submitted_by)
                        : resolveSubmittedByName(architectureReviewLatest?.submitted_by) || "-"}
                    </div>
                    <div className="onboarding-table__cell status">
                      {editingSection === "architectureReview" ? (
                        <div className="onboarding-table__input-wrap">
                          <Dropdown
                            id="architecture_review_status"
                            name="architecture_review_status"
                            label=""
                            options={statusOptions}
                            selected={statusOptions.find((opt) => opt.value === architectureReviewStatus)}
                            onSelect={(opt) => {
                              const nextStatus =
                                (opt?.value as IntegrationStatusLabel) ||
                                architectureReviewStatus;
                              setArchitectureReviewStatus(nextStatus);
                            }}
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            isClearable={false}
                            theme={dropDownThemes.LIGHT}
                            disabled={timelineLocked}
                          />
                        </div>
                      ) : architectureReviewLatest?.status ? (
                        <StatusTag status={architectureReviewLatest.status} />
                      ) : (
                        "-"
                      )}
                    </div>
                    <div className="onboarding-table__cell start">
                      {editingSection === "architectureReview" ? (
                        <div className="onboarding-table__input-wrap">
                          <DatePicker
                            id="architecture_review_start_date"
                            name="architecture_review_start_date"
                            label=""
                            format="dd/MM/yyyy"
                            placeholder="dd/mm/yy"
                            value={architectureReviewStartDate}
                            onChange={(date) =>
                              setArchitectureReviewStartDate(Array.isArray(date) ? date[0] : date)
                            }
                            isRequired={architectureReviewStatus !== "Not applicable"}
                            disabled={timelineLocked}
                          />
                        </div>
                      ) : (
                        formatDateForDisplay(architectureReviewLatest?.start_date ?? null) || "-"
                      )}
                    </div>
                    <div className="onboarding-table__cell completion">
                      {editingSection === "architectureReview" ? (
                        <div className="onboarding-table__input-wrap">
                          <DatePicker
                            id="architecture_review_completion_date"
                            name="architecture_review_completion_date"
                            label=""
                            format="dd/MM/yyyy"
                            placeholder="dd/mm/yy"
                            value={architectureReviewCompletionDate}
                            onChange={(date) =>
                              setArchitectureReviewCompletionDate(Array.isArray(date) ? date[0] : date)
                            }
                            disabled={timelineLocked}
                          />
                        </div>
                      ) : (
                        formatDateForDisplay(architectureReviewLatest?.completion_date ?? null) || "-"
                      )}
                    </div>
                    <div className="onboarding-table__cell comments">
                      <button
                        type="button"
                        className="comments-link"
                        onClick={() => {
                          if (editingSection === "architectureReview") {
                            openCommentsModal("add", undefined, undefined, "architectureReview");
                          } else {
                            openCommentsModal("view", (architectureReviewLatest?.notes as string) ?? "");
                          }
                        }}
                      >
                        {editingSection === "architectureReview"
                          ? "Add"
                          : architectureReviewLatest?.status === "Completed"
                            ? "View"
                            : "Add"}
                      </button>
                    </div>
                    <div className="onboarding-table__cell action">
                      {editingSection === "architectureReview" ? (
                        <div className="onboarding-table__action-btns">
                          <button
                            type="button"
                            className="action-btn"
                            onClick={() => onRowEditConfirm("architectureReview")}
                            disabled={!canSaveArchitectureReview}
                            aria-label="Save"
                          >
                            {status.timelineUpdateLoading ? (
                              <Loader variant={LoaderSizes.small} />
                            ) : (
                              <Iconography icon="check" size={IconographySizes.Small} />
                            )}
                          </button>
                          <button
                            type="button"
                            className="action-btn"
                            onClick={onRowEditCancel}
                            disabled={status.timelineUpdateLoading}
                            aria-label="Cancel"
                          >
                            <Iconography icon="xmark" size={IconographySizes.Small} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="action-btn"
                          onClick={() => onRowEditStart("architectureReview")}
                          disabled={timelineLocked}
                          aria-label="Edit"
                        >
                          <Iconography icon="pen" size={IconographySizes.Small} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <Divider />
          {/* Mid term assessment section */}
          <div className="development-subcategory">
            <div className="subcategory-header">
              <h4 className="subcategory-title">Mid term assessment</h4>
              <div className="subcategory-actions">
                {!midTermAssessmentOpen && (
                  <button
                    type="button"
                    className="status-notes-link"
                    onClick={() => setMidTermAssessmentOpen(true)}
                  >
                    Notes
                  </button>
                )}
                {midTermAssessmentOpen && (
                  <button
                    type="button"
                    className="close-link"
                    onClick={() => setMidTermAssessmentOpen(false)}
                  >
                    
                    <Iconography icon="xmark" size={IconographySizes.Small} />
                  </button>
                )}
              </div>
            </div>
            {midTermAssessmentOpen && (
              <div className="development-table-card onboarding-table-card">
                <div className="onboarding-table">
                  <div className="onboarding-table__row onboarding-table__header">
                    <div className="onboarding-table__cell submitted-by">Submitted by</div>
                    <div className="onboarding-table__cell status">Status</div>
                    <div className="onboarding-table__cell start">Start</div>
                    <div className="onboarding-table__cell completion">Completion</div>
                    <div className="onboarding-table__cell comments">Comments</div>
                    <div className="onboarding-table__cell action">Action</div>
                  </div>
                  <div className="onboarding-table__row onboarding-table__body-row">
                    <div className="onboarding-table__cell submitted-by">
                      {editingSection === "midTermAssessment"
                        ? resolveSubmittedByName(midTermAssessmentLatest?.submitted_by)
                        : resolveSubmittedByName(midTermAssessmentLatest?.submitted_by) || "-"}
                    </div>
                    <div className="onboarding-table__cell status">
                      {editingSection === "midTermAssessment" ? (
                        <div className="onboarding-table__input-wrap">
                          <Dropdown
                            id="mid_term_assessment_status"
                            name="mid_term_assessment_status"
                            label=""
                            options={statusOptions}
                            selected={statusOptions.find((opt) => opt.value === midTermAssessmentStatus)}
                            onSelect={(opt) => {
                              const nextStatus =
                                (opt?.value as IntegrationStatusLabel) ||
                                midTermAssessmentStatus;
                              setMidTermAssessmentStatus(nextStatus);
                            }}
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            isClearable={false}
                            theme={dropDownThemes.LIGHT}
                            disabled={timelineLocked}
                          />
                        </div>
                      ) : midTermAssessmentLatest?.status ? (
                        <StatusTag status={midTermAssessmentLatest.status} />
                      ) : (
                        "-"
                      )}
                    </div>
                    <div className="onboarding-table__cell start">
                      {editingSection === "midTermAssessment" ? (
                        <div className="onboarding-table__input-wrap">
                          <DatePicker
                            id="mid_term_assessment_start_date"
                            name="mid_term_assessment_start_date"
                            label=""
                            format="dd/MM/yyyy"
                            placeholder="dd/mm/yy"
                            value={midTermAssessmentStartDate}
                            onChange={(date) =>
                              setMidTermAssessmentStartDate(Array.isArray(date) ? date[0] : date)
                            }
                            isRequired={midTermAssessmentStatus !== "Not applicable"}
                            disabled={timelineLocked}
                          />
                        </div>
                      ) : (
                        formatDateForDisplay(midTermAssessmentLatest?.start_date ?? null) || "-"
                      )}
                    </div>
                    <div className="onboarding-table__cell completion">
                      {editingSection === "midTermAssessment" ? (
                        <div className="onboarding-table__input-wrap">
                          <DatePicker
                            id="mid_term_assessment_completion_date"
                            name="mid_term_assessment_completion_date"
                            label=""
                            format="dd/MM/yyyy"
                            placeholder="dd/mm/yy"
                            value={midTermAssessmentCompletionDate}
                            onChange={(date) =>
                              setMidTermAssessmentCompletionDate(Array.isArray(date) ? date[0] : date)
                            }
                            disabled={timelineLocked}
                          />
                        </div>
                      ) : (
                        formatDateForDisplay(midTermAssessmentLatest?.completion_date ?? null) || "-"
                      )}
                    </div>
                    <div className="onboarding-table__cell comments">
                      <button
                        type="button"
                        className="comments-link"
                        onClick={() => {
                          if (editingSection === "midTermAssessment") {
                            openCommentsModal("add", undefined, undefined, "midTermAssessment");
                          } else {
                            openCommentsModal("view", (midTermAssessmentLatest?.notes as string) ?? "");
                          }
                        }}
                      >
                        {editingSection === "midTermAssessment"
                          ? "Add"
                          : midTermAssessmentLatest?.status === "Completed"
                            ? "View"
                            : "Add"}
                      </button>
                    </div>
                    <div className="onboarding-table__cell action">
                      {editingSection === "midTermAssessment" ? (
                        <div className="onboarding-table__action-btns">
                          <button
                            type="button"
                            className="action-btn"
                            onClick={() => onRowEditConfirm("midTermAssessment")}
                            disabled={!canSaveMidTermAssessment}
                            aria-label="Save"
                          >
                            {status.timelineUpdateLoading ? (
                              <Loader variant={LoaderSizes.small} />
                            ) : (
                              <Iconography icon="check" size={IconographySizes.Small} />
                            )}
                          </button>
                          <button
                            type="button"
                            className="action-btn"
                            onClick={onRowEditCancel}
                            disabled={status.timelineUpdateLoading}
                            aria-label="Cancel"
                          >
                            <Iconography icon="xmark" size={IconographySizes.Small} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="action-btn"
                          onClick={() => onRowEditStart("midTermAssessment")}
                          disabled={timelineLocked}
                          aria-label="Edit"
                        >
                          <Iconography icon="pen" size={IconographySizes.Small} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {isCommentsModalOpen && (
            <Modal
              headerText={
                commentsModalMode === "view"
                  ? "Comments"
                  : commentsModalMode === "edit"
                    ? "Edit notes"
                    : "Add notes"
              }
              width={500}
              onClose={closeCommentsModal}
              className="comments-modal"
            >
              <div className="modal-content">
                {commentsModalMode === "view" ? (
                  <TextArea
                    id="development_notes_view"
                    name="development_notes_view"
                    label="Notes"
                    placeholder=""
                    rows={4}
                    value={commentsModalNote || "-"}
                    maxLength={500}
                    disabled
                    onChange={(_e: React.ChangeEvent<HTMLTextAreaElement>) => {}}
                  />
                ) : commentsModalMode === "edit" ? (
                  <TextArea
                    id="development_notes_edit"
                    name="development_notes_edit"
                    label="Notes"
                    placeholder="Add notes here..."
                    rows={4}
                    value={commentDraft}
                    maxLength={500}
                    disabled={timelineLocked}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setCommentDraft(e.currentTarget.value.slice(0, 500))
                    }
                  />
                ) : (
                  <TextArea
                    id="development_notes_modal"
                    name="development_notes_modal"
                    label="Notes"
                    placeholder="Add notes here..."
                    rows={4}
                    value={
                      commentsModalAddSection === "architectureReview"
                        ? architectureReviewNotesDraft
                        : midTermAssessmentNotesDraft
                    }
                    maxLength={500}
                    disabled={timelineLocked}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      const v = e.currentTarget.value.slice(0, 500);
                      if (commentsModalAddSection === "architectureReview") setArchitectureReviewNotesDraft(v);
                      else setMidTermAssessmentNotesDraft(v);
                    }}
                  />
                )}
                <div className="modal-footer">
                  <Button
                    variant="secondary"
                    label={commentsModalMode === "view" ? "CLOSE" : "CANCEL"}
                    normalCase={false}
                    onClick={closeCommentsModal}
                  />
                  {(commentsModalMode === "add" || commentsModalMode === "edit") &&
                    !timelineLocked && (
                    <Button
                      variant="primary"
                      label="SAVE"
                      normalCase={false}
                      onClick={() => {
                        if (commentsModalMode === "edit") onSaveCommentEdit();
                        closeCommentsModal();
                      }}
                      disabled={commentsModalMode === "edit" && status.timelineUpdateLoading}
                    />
                  )}
                </div>
              </div>
            </Modal>
          )}

          {canAccessNotesHistory && (
          <>
          <div className="development-edit__footer">
            <button
              type="button"
              className="history-toggle"
              onClick={() => setIsHistoryOpen((v) => !v)}
            >
              <span>
                {isHistoryOpen ? "Notes history" : "Notes history"}
              </span>
              <Iconography
                icon={isHistoryOpen ? "xmark" : "chevronDown"}
                size={IconographySizes.Small}
              />
            </button>
          </div>

          {isHistoryOpen && (
            <div className="development-history-card onboarding-table-card">
              <div className="onboarding-table">
                <div className="onboarding-table__row onboarding-table__header">
                  <div className="onboarding-table__cell submitted-by">Category</div>
                  <div className="onboarding-table__cell submitted-by">Submitted by</div>
                  <div className="onboarding-table__cell status">Status</div>
                  <div className="onboarding-table__cell start">Start</div>
                  <div className="onboarding-table__cell completion">Completion</div>
                  <div className="onboarding-table__cell comments">Comments</div>
                </div>

                {combinedHistoryEntries.length === 0 ? (
                  <div className="onboarding-table__row onboarding-table__body-row onboarding-table__empty">
                    <div className="onboarding-table__cell" style={{ width: "100%", textAlign: "center", padding: "24px" }}>
                      No history available
                    </div>
                  </div>
                ) : (
                  combinedHistoryEntries.map((entry, idx) => (
                    <div className="onboarding-table__row onboarding-table__body-row" key={idx}>
                      <div className="onboarding-table__cell submitted-by">
                        {entry.category || "-"}
                      </div>
                      <div className="onboarding-table__cell submitted-by">
                        {resolveSubmittedByName(entry.submitted_by)}
                      </div>
                      <div className="onboarding-table__cell status">
                        <StatusTag status={entry.status || "Not applicable"} />
                      </div>
                      <div className="onboarding-table__cell start">
                        {formatDateForDisplay(entry.start_date ?? null) || "-"}
                      </div>
                      <div className="onboarding-table__cell completion">
                        {formatDateForDisplay(entry.completion_date ?? null) || "-"}
                      </div>
                      <div className="onboarding-table__cell comments">
                        <button
                          type="button"
                          className="comments-link"
                          onClick={() =>
                            openCommentsModal(
                              timelineLocked ? "view" : "edit",
                              (entry.notes as string) ?? "",
                              idx
                            )
                          }
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          </>
          )}
        </div>
      ) : (
        <div className="development-view-mode">
          {/* Architecture review section */}
          <div className="development-subcategory-section">
            <h4 className="subcategory-title">Architecture review</h4>
            <div className="development-partner-table-card">
              <div className="development-grid">
                <div className="grid-row grid-header">
                  {/* <div className="grid-cell grid-cell--process">Process</div> */}
                  <div className="grid-cell">Submitted by</div>
                  <div className="grid-cell grid-cell--status">Status</div>
                  <div className="grid-cell">Start Date</div>
                  <div className="grid-cell">Completion Date</div>
                </div>

                {architectureReview.map((entry) => (
                  <div className="grid-row" key={entry.id}>
                    {/* <div className="grid-cell grid-cell--process">Architecture review</div> */}
                    <div className="grid-cell">{resolveSubmittedByName(entry.submitted_by)}</div>
                    <div className="grid-cell grid-cell--status"><StatusTag status={entry.status || "Not applicable"} /></div>
                    <div className="grid-cell">{formatDateForDisplay(entry.start_date || null)}</div>
                    <div className="grid-cell">{formatDateForDisplay(entry.completion_date || null)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mid term assessment section */}
          <div className="development-subcategory-section">
            <h4 className="subcategory-title">Mid term assessment</h4>
            <div className="development-partner-table-card">
              <div className="development-grid">
                <div className="grid-row grid-header">
                  {/* <div className="grid-cell grid-cell--process">Process</div> */}
                  <div className="grid-cell">Submitted by</div>
                  <div className="grid-cell grid-cell--status">Status</div>
                  <div className="grid-cell">Start Date</div>
                  <div className="grid-cell">Completion Date</div>
                </div>

                {midTermAssessment.map((entry) => (
                  <div className="grid-row" key={entry.id}>
                    {/* <div className="grid-cell grid-cell--process">
                      Mid term assessment
                    </div> */}
                    <div className="grid-cell">{resolveSubmittedByName(entry.submitted_by)}</div>
                    <div className="grid-cell grid-cell--status"><StatusTag status={entry.status || "Not applicable"} /></div>
                    <div className="grid-cell">{formatDateForDisplay(entry.start_date || null)}</div>
                    <div className="grid-cell">{formatDateForDisplay(entry.completion_date || null)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevelopmentStep;
